import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, View, ActivityIndicator, TouchableOpacity, Switch, Image } from 'react-native';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import MyRadio from '../../../components/MyRadio';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faInfoCircle, faUpRightFromSquare, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Slider } from '@miblanchard/react-native-slider';
import DisableEnableModal from './DisableEnableModal';
import Tooltip from '../../../components/Tooltip';
import Toast from 'react-native-toast-message';
import { RouteProp, useNavigationState, useRoute } from '@react-navigation/native';
import { DuplicatedCameraValue, crmService } from '../../../services/central-api/crm';
import { UnistylesTheme, useStyles } from 'react-native-unistyles';
import DeleteModal from '../../../components/DeleteModal';
import FormInput from '../../../components/FormInput';
import PasswordInput from '../../../components/PasswordInput';
import { ClientError } from '../../../services/central-api/base-service';
import CustomButton from '../../../components/CustomButton';
import AlertGroupsDropdown from '../../../components/Dropdowns/AlertGroupsDropdown';
import PasswordModal from './PasswordModal';
import { useAuth } from '../../../context/Auth';
import InstallationCompanyDropdown from './installationCompanyDropdown';
import InternetCompanyDropdown from './InternetCompanyDropdown';
import Map from '../../../components/Map';
import { WindowInformation } from '../../../services/window-information';
import LocationSelector from '../../../components/LocationSelector';
import { centralAPI } from '../../../services/central-api';
import CameraManufacturerDropdown from '../../../components/crm/CameraManufacturerDropdown';
import VideoStorageTimeDropdown from '../../../components/crm/VideoStorageDropdown';
import FactoryResetModal from './FactoryResetModal';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { useForm, useWatch } from 'react-hook-form';
import WarningModal from '../../../components/WarningModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import AreaInvasionModal from './AreaInvasionModal';
import { CameraType } from '../../../schemas/cameras';
import { Manufacturer } from '../../../schemas/crm';
import { useCurrentApp } from '../../../context/CurrentApp';
import CameraTypeDropdown from '../../cameras/Cameras/CameraTypeDropdown';
import { faEye } from '@fortawesome/pro-regular-svg-icons';
import { CRMParamList } from '../../../typings/Params';
import { Tabs } from '../../../components/Tabs';
import SynchronizationsTab from './SynchronizationsTab';
import ForceSynchronizationModal from './ForceSynchronizationModal';
import { RootNavigation } from '../../../Routes';
import { openInNewTab } from '../../../services/navigation-assistant';

const VANDALISM_CONFIG: Record<Manufacturer, VandalismConfiguration> = {
    hikvision: {
        sceneChange: { minSensitivity: 0, maxSensitivity: 100 },
        defocus: { minSensitivity: 0, maxSensitivity: 100 },
    },
    dahua: {
        videoTampering: { minSensitivity: 0, maxSensitivity: 6 },
        sceneChange: { minSensitivity: 0, maxSensitivity: 5 },
        defocus: { minSensitivity: 0, maxSensitivity: 100 },
    },
    intelbras: {},
};

const DEFAULT_FORM_VALUES: CameraFormFields = {
    title: '',
    address: '',
    httpAddress: undefined,
    serialNo: null,
    type: 'pinned',
    internetType: '4g',
    installationCompanyId: 0,
    internetCompanyId: 0,
    alertGroupId: null,
    rtspAddress: null,
    installer: '',
    installedAmountCameras: 0,
    networkType: 'ipv6',
    sourceTech: 'rtsp',
    hasLpr: false,
    hasFacial: false,
    hasAreaInvasion: false,
    databaseHasAreaInvasion: false,
    riskArea: false,
    macNumber: '',
    chipNumber: '',
    shelterId: '',
    stolen: false,
    ptzConfiguration: undefined,
    manufacturer: 'hikvision',
    videoStorageTime: '15',
    cameraConfiguration: {},
    companyId: undefined,
    disabledAt: null,
    isThirdParty: false,
};

interface CameraFormFields {
    title: string;
    address: string;
    httpAddress: string | undefined | null;
    serialNo: string | null;
    type: CameraType;
    internetType: InternetType;
    installationCompanyId: number | undefined;
    internetCompanyId: number | undefined;
    alertGroupId: number | null;
    rtspAddress: string | null;
    installer: string;
    installedAmountCameras: number;
    networkType: NetworkType;
    sourceTech: SourceTech;
    hasLpr: boolean;
    hasFacial: boolean;
    hasAreaInvasion: boolean;
    databaseHasAreaInvasion: boolean;
    riskArea: boolean;
    macNumber: string;
    chipNumber: string;
    shelterId: string;
    stolen: boolean;
    ptzConfiguration?: PtzConfiguration;
    manufacturer: Manufacturer;
    videoStorageTime: VideoStorageTime;
    cameraConfiguration: CameraConfiguration;
    companyId?: number;
    disabledAt: Date | null;
    isThirdParty: boolean;
}

interface SensitivitySliderProps {
    label: string;
    fieldName: keyof CameraConfiguration & string;
    max: number;
    value: number;
    onValueChange: (v: number) => void;
    disabled: boolean;
    theme: UnistylesTheme;
}

function SensitivitySlider({ label, max, value, onValueChange, disabled, theme }: SensitivitySliderProps) {
    return (
        <View>
            <MyAppText style={{ fontSize: 14 }}>{label}</MyAppText>
            <Slider
                animateTransitions
                minimumValue={0}
                maximumValue={max}
                minimumTrackTintColor={theme.colors.neutral['400']}
                thumbTintColor={theme.colors.neutral['400']}
                maximumTrackTintColor={theme.colors.neutral['300']}
                value={value}
                step={1}
                onValueChange={([v]: number[]) => onValueChange(v)}
                disabled={disabled}
            />
            <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                <MyAppText style={{ fontWeight: 'bold' }}>{value.toFixed(0)}</MyAppText>
            </View>
        </View>
    );
}

export default function ManagementDetails({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<CRMParamList, 'ManagementDetails'>>();

    const { theme } = useStyles();
    const { user: authenticatedUser } = useAuth();
    const { isParentApp } = useCurrentApp();
    const windowInfo = WindowInformation();

    const [showStolenModal, setShowStolenModal] = useState(false);
    const [isFactoryResetVisible, setIsFactorResetVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [alreadyLoaded, setAlreadyLoaded] = useState(false);
    const [isPasswordModalVisible, setPasswordModalVisible] = useState(false);
    const [isDisableEnableModalVisible, setDisableEnableModalVisible] = useState(false);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [isForceSynchronizationModalVisible, setForceSynchronizationModalVisible] = useState(false);
    const [pin, setPin] = useState<LatLng | undefined>();
    const [cameraPassword, setCameraPassword] = useState('');
    const [cameraMetadata, setCameraMetadata] = useState<CameraMetadata | null>(null);

    const index = useNavigationState(state => state);
    const [isDrawingAreaModalVisible, setIsDrawingAreaModalVisible] = useState(false);

    const form = useForm<CameraFormFields>({
        defaultValues: DEFAULT_FORM_VALUES,
        mode: 'onChange',
    });

    const networkType = useWatch({ control: form.control, name: 'networkType' });
    const internetType = useWatch({ control: form.control, name: 'internetType' });
    const cameraType = useWatch({ control: form.control, name: 'type' });
    const manufacturer = useWatch({ control: form.control, name: 'manufacturer' });
    const companyId = useWatch({ control: form.control, name: 'companyId' });
    const hasLpr = useWatch({ control: form.control, name: 'hasLpr' });
    const hasFacial = useWatch({ control: form.control, name: 'hasFacial' });
    const hasAreaInvasion = useWatch({ control: form.control, name: 'hasAreaInvasion' });
    const riskArea = useWatch({ control: form.control, name: 'riskArea' });
    const stolen = useWatch({ control: form.control, name: 'stolen' });
    const disabledAt = useWatch({ control: form.control, name: 'disabledAt' });
    const isThirdParty = useWatch({ control: form.control, name: 'isThirdParty' });
    const httpAddress = useWatch({ control: form.control, name: 'httpAddress' });
    const rtspAddress = useWatch({ control: form.control, name: 'rtspAddress' });
    const installedAmountCameras = useWatch({ control: form.control, name: 'installedAmountCameras' });
    const ptzConfiguration = useWatch({ control: form.control, name: 'ptzConfiguration' });
    const alertGroupId = useWatch({ control: form.control, name: 'alertGroupId' });
    const cameraConfiguration = useWatch({ control: form.control, name: 'cameraConfiguration' });
    const title = useWatch({ control: form.control, name: 'title' });
    const serialNo = useWatch({ control: form.control, name: 'serialNo' });

    const canEditFields = useMemo(
        () => !!(authenticatedUser?.isAdmin || authenticatedUser?.permissions.crm_module_access),
        [authenticatedUser]
    );

    const isFormValid = useMemo(() => {
        if (!installedAmountCameras || installedAmountCameras < 1) {
            return false;
        }
        if (networkType === 'ipv4') {
            if (!rtspAddress) {
                return false;
            }
            if (!httpAddress) {
                return false;
            }
        }

        if (!route.params?.id && !serialNo) {
            return false;
        }

        if (cameraType === 'ptz') {
            if (!ptzConfiguration?.username) {
                return false;
            }
            if (!ptzConfiguration?.password) {
                return false;
            }
        }
        if (hasAreaInvasion && !alertGroupId) {
            return false;
        }
        if (pin && (Number.isNaN(pin.lat) || Number.isNaN(pin.lng))) {
            return false;
        }
        return true;
    }, [installedAmountCameras, networkType, rtspAddress, httpAddress, cameraType, ptzConfiguration, hasAreaInvasion, alertGroupId, pin, serialNo]);

    const goBack = useCallback(() => {
        if (index.routes.length > 1) {
            navigation.goBack();
        } else {
            navigation.navigate('CRM', { screen: 'Management' });
        }
    }, [index.routes.length]);

    useEffect(() => {
        if (cameraType == 'lpr') {
            form.setValue('hasLpr', true);
            form.setValue('hasFacial', false);
            form.setValue('hasAreaInvasion', false);
            form.setValue('alertGroupId', null);
        }
    }, [cameraType]);

    function getDefaultConfiguration(manufacturer: Manufacturer): CameraConfiguration | undefined {
        if (!isParentApp) {
            return undefined;
        }

        const configs: Partial<Record<Manufacturer, CameraConfiguration>> = {
            hikvision: {
                videoTamperingSensitivity: '0',
                sceneChangeSensitivity: '55',
                defocusSensitivity: '55'
            },
            dahua: {
                videoTamperingSensitivity: '0',
                sceneChangeSensitivity: '5',
                defocusSensitivity: '70'
            },
        };

        return configs[manufacturer] ?? {
            videoTamperingSensitivity: '0',
            sceneChangeSensitivity: '0',
            defocusSensitivity: '0',
        };
    }

    async function fetchAddress(pos: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pos.lat, longitude: pos.lng });
        if (address) {
            form.setValue('address', address);
        }
    }

    async function getCamera(id: string) {
        try {
            setIsLoading(true);
            const camera = await crmService.getCamera(id);
            fetchAddress({ lat: camera.latitude, lng: camera.longitude });
            setPin({ lat: camera.latitude, lng: camera.longitude });
            setCameraMetadata(camera.cameraMetadata ?? null);
            form.reset({
                title: camera.title,
                address: camera.address,
                httpAddress: camera.httpAddress,
                serialNo: camera.serialNo,
                type: camera.type,
                internetType: camera.internetType,
                installationCompanyId: camera.installationCompanyId ?? 0,
                internetCompanyId: camera.internetCompanyId ?? 0,
                alertGroupId: camera.alertGroupId,
                rtspAddress: camera.rtspAddress,
                installer: camera.installer,
                installedAmountCameras: camera.installedAmountCameras,
                networkType: camera.networkType,
                sourceTech: camera.sourceTech,
                hasLpr: camera.hasLpr,
                hasFacial: camera.hasFacial,
                hasAreaInvasion: camera.hasAreaInvasion,
                databaseHasAreaInvasion: camera.hasAreaInvasion,
                macNumber: camera.macNumber ?? '',
                chipNumber: camera.chipNumber ?? '',
                shelterId: camera.shelterId ?? '',
                stolen: camera.stolen,
                ptzConfiguration: camera.ptzConfiguration,
                manufacturer: camera.manufacturer,
                videoStorageTime: camera.videoStorageTime,
                cameraConfiguration: camera.cameraConfiguration ?? getDefaultConfiguration(camera.manufacturer),
                isThirdParty: !!(camera.thirdPartyCamera || camera.originalCamera),
                companyId: camera.companyId,
                riskArea: camera.riskArea,
                disabledAt: camera.disabledAt
            });
        } catch (err) {
            console.error(err);
        } finally {
            setAlreadyLoaded(true);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (route.params?.id && !alreadyLoaded) {
            getCamera(route.params?.id);
        }
    }, [route.params]);

    function buildPtzConfiguration(data: CameraFormFields): PtzConfiguration | undefined {
        if (data.type !== 'ptz') {
            return undefined;
        }
        return {
            username: data.ptzConfiguration?.username ?? '',
            password: data.ptzConfiguration?.password ?? '',
        };
    }

    async function save() {
        try {
            const data = form.getValues();
            setIsLoading(true);

            const resolvedRtspAddress = data.networkType === 'ipv4' && data.sourceTech === 'rtsp' && data.rtspAddress
                ? data.rtspAddress
                : undefined;
            const resolvedHttpAddress = data.networkType === 'ipv4' && data.httpAddress
                ? data.httpAddress
                : undefined;

            const sharedPayload = {
                title: data.title,
                address: data.address,
                latitude: Number(pin?.lat),
                longitude: Number(pin?.lng),
                hasFacial: data.hasFacial,
                hasLpr: data.hasLpr,
                hasAreaInvasion: data.hasAreaInvasion,
                internetType: data.internetType,
                installer: data.installer,
                installationCompanyId: data.installationCompanyId,
                internetCompanyId: data.internetCompanyId,
                alertGroupId: data.alertGroupId ?? null,
                sourceTech: data.sourceTech,
                rtspAddress: resolvedRtspAddress,
                httpAddress: resolvedHttpAddress,
                networkType: data.networkType,
                installedAmountCameras: Number(data.installedAmountCameras),
                ptzConfiguration: buildPtzConfiguration(data),
                riskArea: data.riskArea,
                macNumber: data.macNumber,
                chipNumber: data.chipNumber,
                shelterId: data.shelterId,
                manufacturer: data.manufacturer,
                cameraConfiguration: data.cameraConfiguration,
                videoStorageTime: data.videoStorageTime,
            };

            if (route.params?.id) {
                await crmService.updateCamera(route.params?.id, {
                    ...sharedPayload,
                    serialNo: data.serialNo ?? undefined,
                    stolen: data.stolen,
                });
            } else if (data.serialNo && sharedPayload.address) {
                await crmService.createCamera({
                    ...sharedPayload,
                    type: data.type,
                    serialNo: data.serialNo,
                    stolen: false,
                    internetCompanyId: data.internetCompanyId ?? 0,
                    installationCompanyId: data.installationCompanyId ?? 0
                });
            }

            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            goBack();
        } catch (err) {
            if (err instanceof DuplicatedCameraValue) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message, { cameraId: err.cameraId }),
                    visibilityTime: 10000,
                });
            } else if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else if (err instanceof TypeError) {
                Toast.show({ type: 'sentinelxError', text1: translate('InvalidHttpAddress') });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        } finally {
            setIsLoading(false);
        }
    }

    async function getCameraPassword() {
        try {
            setPasswordModalVisible(true);
            const result = await crmService.getCameraPassword(route.params?.id ?? '');
            setCameraPassword(result.password);
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    const dropDownOptions = useMemo<Action[]>(() => {
        const actions: Action[] = [];
        const cameraId = route.params?.id;
        if (!cameraId) {
            return [];
        }

        actions.push({
            content: translate('watchCamera'),
            onPress: () => navigation.navigate('Cameras', { screen: 'CameraPlayer', params: { id: cameraId } }),
        }, {
            content: translate('watchCameraOnNewTab'),
            onPress: () => {
                openInNewTab('Cameras', { screen: 'CameraMapPlayer', params: { id: cameraId } });
            }
        });

        if (authenticatedUser?.permissions.get_camera_password || authenticatedUser?.isAdmin) {
            actions.push({ content: translate('getCameraPassword'), onPress: getCameraPassword });
        }

        if (hasLpr && (authenticatedUser?.isAdmin || authenticatedUser?.permissions.lpr_module_access)) {
            actions.push({
                content: translate('watchLprEvents'),
                onPress: () => navigation.navigate('LPR', { screen: 'LprLiveEvents', params: { cameras: [cameraId] } }),
            });
        }

        if (hasFacial && (
            authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.facial_module_access
            || authenticatedUser?.permissions.facial_events_missing_person
            || authenticatedUser?.permissions.facial_events_wanted_person
        )) {
            actions.push({
                content: translate('watchFacialEvents'),
                onPress: () => navigation.navigate('Facial', { screen: 'LiveEvents', params: { cameras: [cameraId] } }),
            });
        }

        if (isParentApp && canEditFields) {
            actions.push({
                content: translate('forceSynchronization'),
                onPress: () => setForceSynchronizationModalVisible(true),
            });
        }

        if (canEditFields) {
            actions.push({
                content: translate('informFactoryReset'),
                onPress: () => setIsFactorResetVisible(true),
                variant: 'danger'
            }, {
                content: disabledAt !== null ? translate('enable') : translate('disable'),
                onPress: () => setDisableEnableModalVisible(true),
                variant: disabledAt !== null ? 'success' : 'danger',
            }, {
                content: translate('delete'),
                onPress: () => setDeleteModalVisible(true),
                variant: 'danger'
            });
        }

        return actions;
    }, [route.params?.id, canEditFields, authenticatedUser, hasLpr, hasFacial, disabledAt]);

    if (isThirdParty) {
        return (
            <Container>
                <Header title={translate('camera')} goBackAction={goBack} />
                <Card>
                    <MyAppText style={{ fontSize: 12 }}>{translate('thirdPartyCameraWarning')}</MyAppText>
                </Card>
            </Container>
        );
    }

    const vandalismConfig = manufacturer ? VANDALISM_CONFIG[manufacturer] : undefined;

    function sliderValue(field: keyof CameraConfiguration): number {
        return Number(cameraConfiguration?.[field] ?? 0);
    }

    return (
        <Container>
            <Header
                title={translate('camera')}
                goBackAction={goBack}
                actions={
                    <>
                        {dropDownOptions.length > 0 && (
                            <DropdownButton
                                buttonLabel='actions'
                                buttonIcon={faChevronDown}
                                isButton
                                actions={dropDownOptions}
                                offsetX={-97}
                                offsetY={41}
                                size={200}
                            />
                        )}
                        {canEditFields && (
                            <CustomButton
                                text={translate('save')}
                                disabled={!isFormValid}
                                onPress={() => {
                                    if (form.getFieldState('stolen').isDirty && form.getValues().stolen && disabledAt === null) {
                                        setShowStolenModal(true);
                                    } else {
                                        save();
                                    }
                                }}
                            />
                        )}
                    </>
                }
            />

            <Tabs
                tabs={[{
                    key: 'general_info',
                    label: translate('general_info'),
                }, {
                    key: 'synchronizations',
                    label: translate('synchronizations'),
                    omit: !isParentApp
                }]}>
                {({ selectedTab }) => {
                    if (selectedTab == 'general_info') {
                        return <Card>
                            <ScrollView style={{ padding: 24, height: '100%' }} contentContainerStyle={{ rowGap: 15, height: '100%' }}>
                                {isLoading && <ActivityIndicator size='small' color={theme.colors.iconColor} />}

                                <View style={{ flex: 1, flexWrap: 'wrap', flexDirection: 'row', columnGap: 40, rowGap: 10, height: '100%' }}>
                                    <View style={[!windowInfo.isMobile && { flex: 1 }, { rowGap: 15, maxWidth: 400, width: '100%', zIndex: 11 }]}>
                                        <FormInput
                                            label={translate('title')}
                                            placeholder=''
                                            form={form}
                                            fieldName='title'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            required
                                        />
                                        <LocationSelector
                                            label={translate('address')}
                                            address={form.watch('address')}
                                            onAddressChange={(address) => form.setValue('address', address.toString())}
                                            onPinChange={pos => setPin(pos)}
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            viewStyle={{ zIndex: 10 }}
                                            required
                                        />
                                        <CameraManufacturerDropdown
                                            form={form}
                                            fieldName='manufacturer'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            zIndex={9}
                                            required
                                        />
                                        <VideoStorageTimeDropdown
                                            form={form}
                                            fieldName='videoStorageTime'
                                            disabled={!isParentApp}
                                            zIndex={8}
                                            labelFontSize={14}
                                            required
                                        />
                                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                            <View style={{ width: '95%' }}>
                                                <FormInput
                                                    label={translate('serialNo')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='serialNo'
                                                    disabled={!canEditFields}
                                                    maxLength={9}
                                                    labelFontSize={14}
                                                    required
                                                />
                                            </View>
                                            <View style={{ paddingTop: 24 }}>
                                                <Tooltip
                                                    tooltipDirection='right'
                                                    style={{ width: 600 }}
                                                    tooltipContent={
                                                        <View style={{ flexDirection: 'column', rowGap: 10, alignItems: 'center', paddingVertical: 5 }}>
                                                            <MyAppText style={{ color: theme.colors.labelColor }}>{translate('hikSerialNoExplanation')}</MyAppText>
                                                            <Image
                                                                source={require('../../../../assets/hikvision_serial_no.png')}
                                                                style={{ height: 300, width: 580, objectFit: 'contain' }}
                                                            />
                                                        </View>
                                                    }
                                                >
                                                    <FontAwesomeIcon icon={faInfoCircle} color={theme.colors.iconColor} />
                                                </Tooltip>
                                            </View>
                                        </View>

                                        <View style={{ rowGap: 5 }}>
                                            <View style={{ flexDirection: 'row', gap: 3 }}>
                                                <MyAppText style={{ fontSize: 14 }}>{translate('networkType')}</MyAppText>
                                                <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                            </View>
                                            <MyRadio
                                                editable={canEditFields}
                                                selected={networkType}
                                                setSelected={(value: NetworkType) => form.setValue('networkType', value)}
                                                options={[{ value: 'ipv6', label: 'IPv6' }, { value: 'ipv4', label: 'IPv4' }]}
                                                labelFontSize={14}
                                            />
                                        </View>

                                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                            <View style={cameraType === 'panoramic' ? { width: '95%' } : { width: '100%' }}>
                                                <FormInput
                                                    label={translate('rtspAddress')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='rtspAddress'
                                                    disabled={!canEditFields || networkType !== 'ipv4'}
                                                    labelFontSize={14}
                                                    required={networkType === 'ipv4'}
                                                />
                                            </View>
                                            {cameraType === 'panoramic' && (
                                                <View style={{ paddingTop: 24 }}>
                                                    <Tooltip
                                                        tooltipDirection='right'
                                                        style={{ width: 300 }}
                                                        tooltipContent={
                                                            <View style={{ paddingVertical: 5 }}>
                                                                <MyAppText>{translate('rtspIpv4Panoramic')}</MyAppText>
                                                            </View>
                                                        }
                                                    >
                                                        <FontAwesomeIcon icon={faInfoCircle} />
                                                    </Tooltip>
                                                </View>
                                            )}
                                        </View>

                                        {(route.params?.id || networkType === 'ipv4') && (
                                            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 5 }}>
                                                <View style={route.params?.id ? { width: '95%' } : { width: '100%' }}>
                                                    <FormInput
                                                        label={translate('httpAddress')}
                                                        placeholder=''
                                                        form={form}
                                                        fieldName='httpAddress'
                                                        disabled={networkType !== 'ipv4'}
                                                        invalid={() => false}
                                                        labelFontSize={14}
                                                        required
                                                    />
                                                </View>
                                                {route.params?.id && (
                                                    <View style={{ paddingTop: 24 }}>
                                                        <TouchableOpacity onPress={() => {
                                                            const a = document.createElement('a');
                                                            const href = httpAddress as string;
                                                            a.href = href.includes('http') ? href : `http://${href}`;
                                                            a.target = '_blank';
                                                            a.click();
                                                        }}>
                                                            <FontAwesomeIcon icon={faUpRightFromSquare} color={theme.colors.iconColor} />
                                                        </TouchableOpacity>
                                                    </View>
                                                )}
                                            </View>
                                        )}

                                        <InstallationCompanyDropdown
                                            form={form}
                                            fieldName='installationCompanyId'
                                            zIndex={7}
                                            disabled={!authenticatedUser?.isAdmin && !!route.params?.id}
                                            companyId={isParentApp ? companyId : undefined}
                                            labelFontSize={14}
                                            required
                                        />
                                        <FormInput
                                            label={translate('installer')}
                                            placeholder=''
                                            form={form}
                                            fieldName='installer'
                                            disabled={!authenticatedUser?.isAdmin && !!route.params?.id}
                                            labelFontSize={14}
                                            required
                                        />

                                        <View style={{ rowGap: 5 }}>
                                            <View style={{ flexDirection: 'row', gap: 3 }}>
                                                <MyAppText style={{ fontSize: 14 }}>{translate('internetType')}</MyAppText>
                                                <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                            </View>
                                            <MyRadio
                                                selected={internetType}
                                                editable={canEditFields}
                                                setSelected={(value: InternetType) => {
                                                    form.setValue('internetType', value);
                                                }}
                                                options={[
                                                    { label: translate('fiber'), value: 'fiber' },
                                                    { label: '5G', value: '5g' },
                                                    { label: '4G', value: '4g' },
                                                    { label: translate('radio'), value: 'radio' },
                                                ]}
                                                labelFontSize={14}
                                            />
                                        </View>
                                    </View>
                                    <View style={[!windowInfo.isMobile && { flex: 1 }, { rowGap: 20, maxWidth: 400, width: '100%' }]}>
                                        <InternetCompanyDropdown
                                            form={form}
                                            fieldName='internetCompanyId'
                                            zIndex={6}
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            companyId={isParentApp ? companyId : undefined}
                                            required
                                        />
                                        <FormInput
                                            label={translate('installedAmountCameras')}
                                            placeholder=''
                                            form={form}
                                            fieldName='installedAmountCameras'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            required
                                            mode='NUMBER'
                                            minValue={1}
                                        />
                                        <CameraTypeDropdown
                                            form={form}
                                            fieldName='type'
                                            includeAll={false}
                                            required={true}
                                            disabled={!!route.params?.id}
                                            zIndex={5}
                                        />
                                        <FormInput
                                            label={translate('macNumber')}
                                            placeholder=''
                                            form={form}
                                            fieldName='macNumber'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            required={false}
                                        />
                                        {internetType === '4g' && (
                                            <FormInput
                                                label={translate('chipNumber')}
                                                placeholder=''
                                                form={form}
                                                fieldName='chipNumber'
                                                disabled={!canEditFields}
                                                labelFontSize={14}
                                                required={false}
                                            />
                                        )}
                                        <FormInput
                                            label={translate('ShelterId')}
                                            placeholder=''
                                            form={form}
                                            fieldName='shelterId'
                                            disabled={!canEditFields}
                                            labelFontSize={14}
                                            required={false}
                                        />

                                        <View style={{ rowGap: 5 }}>
                                            <View style={{ flexDirection: 'row', gap: 3 }}>
                                                <MyAppText style={{ fontSize: 14 }}>{translate('algorithm')}</MyAppText>
                                                <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                            </View>
                                            <View style={{ flexDirection: 'row', columnGap: 20 }}>
                                                {([
                                                    { key: 'hasLpr', label: translate('lpr'), value: hasLpr },
                                                    { key: 'hasFacial', label: translate('facial'), value: hasFacial },
                                                    { key: 'hasAreaInvasion', label: translate('area_invasion'), value: hasAreaInvasion },
                                                ] as const).map(({ key, label, value }) => (
                                                    <View key={key}>
                                                        <MyAppText style={{ fontSize: 14 }}>{label}</MyAppText>
                                                        <Switch
                                                            trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                            thumbColor={value ? '#f5dd4b' : '#f4f3f4'}
                                                            disabled={cameraType === 'lpr'}
                                                            onValueChange={(v) => {
                                                                if (canEditFields) {
                                                                    form.setValue('hasLpr', false);
                                                                    form.setValue('hasFacial', false);
                                                                    form.setValue('hasAreaInvasion', false);
                                                                    form.setValue(key, v);
                                                                }
                                                            }}
                                                            value={!!value}
                                                        />
                                                    </View>
                                                ))}
                                            </View>

                                            <MyAppText style={{ fontSize: 14 }}>{translate('stolen_equipment')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={stolen ? '#f5dd4b' : '#f4f3f4'}
                                                disabled={!route.params?.id}
                                                onValueChange={(v) => {
                                                    if (canEditFields || route.params?.id) {
                                                        form.setValue('stolen', v, { shouldDirty: true, shouldTouch: true });
                                                    }
                                                }}
                                                value={!!stolen}
                                            />

                                            <MyAppText style={{ fontSize: 14 }}>{translate('riskArea')}</MyAppText>
                                            <Switch
                                                trackColor={{ false: '#767577', true: '#81b0ff' }}
                                                thumbColor={riskArea ? '#f5dd4b' : '#f4f3f4'}
                                                onValueChange={(v) => {
                                                    if (canEditFields) {
                                                        form.setValue('riskArea', v);
                                                    }
                                                }}
                                                value={!!riskArea}
                                            />
                                        </View>

                                        {hasAreaInvasion && (
                                            <>
                                                {form.watch('databaseHasAreaInvasion') && (
                                                    <CustomButton
                                                        onPress={() => setIsDrawingAreaModalVisible(true)}
                                                        icon={faEye as IconDefinition}
                                                        text={translate('drawnArea')}
                                                    />
                                                )}
                                                <AlertGroupsDropdown form={form} fieldName='alertGroupId' zIndex={5} labelFontSize={14} required />
                                            </>
                                        )}
                                        {cameraType === 'ptz' && (
                                            <>
                                                <FormInput
                                                    label={translate('ptzUsername')}
                                                    placeholder=''
                                                    disabled={!canEditFields}
                                                    form={form}
                                                    fieldName='ptzConfiguration.username'
                                                    labelFontSize={14}
                                                    required
                                                />
                                                <PasswordInput
                                                    label={translate('ptzPassword')}
                                                    placeholder=''
                                                    form={form}
                                                    fieldName='ptzConfiguration.password'
                                                    disabled={!canEditFields}
                                                    labelFontSize={14}
                                                    required
                                                />
                                            </>
                                        )}

                                        {isParentApp && vandalismConfig && (
                                            <View>
                                                {vandalismConfig.videoTampering && (
                                                    <SensitivitySlider
                                                        label={translate('videoTamperingSensitivity')}
                                                        fieldName='videoTamperingSensitivity'
                                                        max={vandalismConfig.videoTampering.maxSensitivity}
                                                        value={sliderValue('videoTamperingSensitivity')}
                                                        onValueChange={(v) => form.setValue('cameraConfiguration.videoTamperingSensitivity', String(v))}
                                                        disabled={!canEditFields}
                                                        theme={theme}
                                                    />
                                                )}
                                                {vandalismConfig.sceneChange && (
                                                    <SensitivitySlider
                                                        label={translate('sceneChangeSensitivity')}
                                                        fieldName='sceneChangeSensitivity'
                                                        max={vandalismConfig.sceneChange.maxSensitivity}
                                                        value={sliderValue('sceneChangeSensitivity')}
                                                        onValueChange={(v) => form.setValue('cameraConfiguration.sceneChangeSensitivity', String(v))}
                                                        disabled={!canEditFields}
                                                        theme={theme}
                                                    />
                                                )}
                                                {vandalismConfig.defocus && (
                                                    <SensitivitySlider
                                                        label={translate('defocusSensitivity')}
                                                        fieldName='defocusSensitivity'
                                                        max={vandalismConfig.defocus.maxSensitivity}
                                                        value={sliderValue('defocusSensitivity')}
                                                        onValueChange={(v) => form.setValue('cameraConfiguration.defocusSensitivity', String(v))}
                                                        disabled={!canEditFields}
                                                        theme={theme}
                                                    />
                                                )}
                                            </View>
                                        )}
                                    </View>
                                    <View style={[!windowInfo.isMobile ? { height: '100%' } : { height: 400, paddingBottom: 20 }, { flex: 1 }]}>
                                        <Map
                                            draggable={canEditFields}
                                            dynamicMarker={pin}
                                            useDynamicMarker
                                            markerChanged={(pos) => {
                                                fetchAddress(pos);
                                                setPin(pos);
                                            }}
                                        />
                                    </View>
                                </View>
                            </ScrollView>
                        </Card>;
                    } else if (selectedTab == 'synchronizations') {
                        return <SynchronizationsTab metadata={cameraMetadata} isLoading={isLoading} />;
                    }
                }}
            </Tabs>

            <DisableEnableModal
                isModalVisible={isDisableEnableModalVisible}
                onClose={(success: boolean) => {
                    setDisableEnableModalVisible(false);
                    if (success) {
                        Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
                    }
                }}
                cameraData={{
                    id: route.params?.id,
                    disabledAt: disabledAt
                }}
                setDisabledAt={() => form.setValue('disabledAt', new Date())}
            />

            <PasswordModal
                isModalVisible={isPasswordModalVisible}
                setIsModalVisible={setPasswordModalVisible}
                cameraPassword={cameraPassword}
            />

            {route.params?.id && (
                <FactoryResetModal
                    cameraId={route.params?.id}
                    setIsModalVisible={setIsFactorResetVisible}
                    isModalVisible={isFactoryResetVisible}
                    onReset={() => {
                        if (route.params?.id) {
                            getCamera(route.params?.id);
                        }
                    }}
                />
            )}

            <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={title}
                onSubmit={async () => {
                    try {
                        if (route.params?.id) {
                            await crmService.deleteCamera(route.params?.id);
                            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
                            goBack();
                        }
                    } catch (err) {
                        if (err instanceof ClientError) {
                            Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
                        } else {
                            console.error(err);
                            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
                        }
                    }
                }}
            />

            {route.params?.id && (
                <ForceSynchronizationModal
                    isModalVisible={isForceSynchronizationModalVisible}
                    setIsModalVisible={setForceSynchronizationModalVisible}
                    cameraId={route.params?.id}
                />
            )}

            <WarningModal
                isModalVisible={showStolenModal}
                setIsModalVisible={setShowStolenModal}
                text={translate('setStolenDisableCamera')}
                onSubmit={() => {
                    setShowStolenModal(false);
                    save();
                }}
                onClose={() => setShowStolenModal(false)}
            />

            <AreaInvasionModal
                isModalVisible={isDrawingAreaModalVisible}
                setIsModalVisible={setIsDrawingAreaModalVisible}
                cameraId={route.params?.id}
            />
        </Container>
    );
}
